import React from "react";
import Reveal from "./Reveal";
import { GraduationCap, School, Building2, ArrowUpRight, MessageCircle, Mail } from "lucide-react";
import { waLink, mailLink, CONTACT, openExternal } from "../lib/contact";

const ROUTES = [
  {
    id: "school",
    label: "School",
    Icon: School,
    tag: "K–12",
    line: "AI literacy for Class 6–12 students, teacher training, and parent orientation.",
  },
  {
    id: "college",
    label: "College",
    Icon: GraduationCap,
    tag: "Higher Ed",
    line: "The full 17-module flagship + Innovation Arena edition on your campus.",
  },
  {
    id: "corporate",
    label: "Corporate",
    Icon: Building2,
    tag: "Workforce",
    line: "Function & leadership tracks, Chief AI Officer, custom case studies on your data.",
  },
];

export default function ReachUs() {
  return (
    <section
      className="relative py-10 md:py-14 reach-band overflow-hidden"
      data-testid="reachus-section"
    >
      {/* Ambient orbs */}
      <span className="orb" style={{ width: 420, height: 420, top: -120, left: "-6%", background: "#4F46E5" }} />
      <span className="orb" style={{ width: 360, height: 360, bottom: -120, right: "-6%", background: "#C9A961", opacity: 0.35 }} />

      <div className="container-edge relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <p
                className="font-mono text-[11px] mb-5"
                style={{ color: "var(--accent-gold)", letterSpacing: "0.18em" }}
              >
                REACH US — AI TRAINING FOR YOUR TEAM
              </p>
            </Reveal>
            <Reveal>
              <h2
                className="font-display text-[30px] sm:text-[40px] lg:text-[48px] leading-[1.04] tracking-tight"
                style={{ fontWeight: 400, color: "var(--canvas)" }}
              >
                Want AI training for your{" "}
                <em className="italic" style={{ fontWeight: 400, color: "var(--accent-gold)" }}>
                  school, college, or company?
                </em>
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p
                className="mt-6 text-[17px] md:text-[19px] leading-relaxed max-w-[620px]"
                style={{ color: "rgba(250,250,247,0.78)" }}
              >
                Tell us who you are. We'll send back a scoped proposal — cohort size, channels,
                certifications, and an Innovation Arena edition tailored to your context — in 48 hours.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={160}>
              <a
                href="#enquiry"
                className="btn-gold w-full sm:w-auto text-[16px] px-7 py-4"
                data-testid="reachus-primary-cta"
              >
                Talk to us — get a proposal in 48 hrs →
              </a>
            </Reveal>

            <Reveal delay={220}>
              <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="reachus-direct-channels">
                <a
                  href={waLink("Hi AI For Everyone — I'd like to talk about AI training for my team.")}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium rounded-sm transition-colors"
                  style={{
                    color: "var(--canvas)",
                    background: "rgba(37,211,102,0.14)",
                    border: "1px solid rgba(37,211,102,0.5)",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#25D366")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(37,211,102,0.14)")}
                  data-testid="reachus-whatsapp"
                >
                  <MessageCircle size={16} strokeWidth={1.75} />
                  WhatsApp us
                </a>
                <a
                  href={mailLink({
                    subject: "Enquiry — AI training",
                    body: "Hi, I'd like to learn more about AI For Everyone for my organization.",
                  })}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium rounded-sm transition-colors"
                  style={{
                    color: "var(--canvas)",
                    border: "1px solid rgba(250,250,247,0.3)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--canvas)";
                    e.currentTarget.style.color = "var(--ink)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "var(--canvas)";
                  }}
                  data-testid="reachus-email"
                >
                  <Mail size={16} strokeWidth={1.75} />
                  Email us
                </a>
                <span
                  className="font-mono text-[11px] hidden sm:inline"
                  style={{ color: "rgba(250,250,247,0.5)", letterSpacing: "0.08em" }}
                >
                  · or call {CONTACT.phoneDisplay}
                </span>
              </div>
            </Reveal>
          </div>
        </div>

        {/* 3 quick routes */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {ROUTES.map(({ id, label, Icon, tag, line }, i) => (
            <Reveal key={id} delay={i * 90}>
              <a
                href={`#enquiry?as=${id}`}
                onClick={(e) => {
                  // Soft-prefill: dispatch a custom event the form can listen to,
                  // and let the anchor still jump to the form.
                  try {
                    window.dispatchEvent(new CustomEvent("enquiry:prefill", { detail: { audience: id } }));
                  } catch (_) {}
                }}
                className="group relative block p-7 lg:p-8 rounded-sm overflow-hidden transition-all"
                style={{
                  background: "rgba(250,250,247,0.04)",
                  border: "1px solid rgba(250,250,247,0.14)",
                  backdropFilter: "blur(6px)",
                }}
                data-testid={`reachus-route-${id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div
                    className="w-11 h-11 inline-flex items-center justify-center rounded-sm"
                    style={{ background: "rgba(201,169,97,0.16)", color: "var(--accent-gold)" }}
                  >
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <ArrowUpRight
                    size={18}
                    strokeWidth={1.5}
                    className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    style={{ color: "var(--canvas)" }}
                  />
                </div>
                <p
                  className="mt-7 font-mono text-[11px]"
                  style={{ color: "var(--accent-gold)", letterSpacing: "0.16em" }}
                >
                  {tag.toUpperCase()}
                </p>
                <h3
                  className="mt-2 font-display text-[28px] leading-tight"
                  style={{ fontWeight: 500, color: "var(--canvas)" }}
                >
                  {label}
                </h3>
                <p
                  className="mt-3 text-[14px] leading-relaxed"
                  style={{ color: "rgba(250,250,247,0.7)" }}
                >
                  {line}
                </p>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
